import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { appointmentsAPI, doctorsAPI } from '../services/api';
import { Calendar, Plus, Search, MapPin, Mail, Phone, User, Stethoscope, X, Building, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const DoctorCard = ({ doctor, onBook, onDelete, isAdmin }) => {
  return (
    <div className="bg-card rounded-2xl p-6 border border-border hover:border-primary/50 transition-all" data-testid="doctor-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            {doctor.name.includes('Clinic') || doctor.name.includes('Dept') ? (
              <Building className="w-7 h-7 text-primary" />
            ) : (
              <User className="w-7 h-7 text-primary" />
            )}
          </div>
          <div>
            <h3 className="font-bold text-foreground text-lg">{doctor.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Stethoscope className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-primary font-medium">{doctor.specialty}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-info/10 text-info">
            {doctor.location}
          </span>
          {isAdmin && (
            <button
              onClick={() => onDelete(doctor.id)}
              className="p-2 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20"
              data-testid="delete-doctor-btn"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      <div className="space-y-3 mb-4">
        <div className="flex items-start gap-3">
          <MapPin className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
          <span className="text-sm text-foreground">{doctor.address}</span>
        </div>
        <div className="flex items-center gap-3">
          <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <a href={`tel:${doctor.contact.split('/')[0].trim()}`} className="text-sm text-foreground hover:text-primary">
            {doctor.contact}
          </a>
        </div>
        <div className="flex items-center gap-3">
          <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <a href={`mailto:${doctor.email}`} className="text-sm text-primary hover:underline">
            {doctor.email}
          </a>
        </div>
      </div>
      
      <button
        onClick={() => onBook(doctor)}
        className="w-full py-3 rounded-xl bg-primary/10 text-primary font-semibold hover:bg-primary/20 transition-colors"
        data-testid="book-doctor-btn"
      >
        Book Appointment
      </button>
    </div>
  );
};

const AddDoctorModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    email: '',
    contact: '',
    address: '',
    location: 'Chandigarh'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onAdd(formData);
    setFormData({ name: '', specialty: '', email: '', contact: '', address: '', location: 'Chandigarh' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl p-6 w-full max-w-md border border-border" data-testid="add-doctor-modal">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Add New Doctor
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Name *</label>
            <input
              type="text"
              required
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Dr. John Doe"
              data-testid="doctor-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Specialty *</label>
            <input
              type="text"
              required
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground"
              value={formData.specialty}
              onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
              placeholder="Psychiatrist"
              data-testid="doctor-specialty-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
            <input
              type="email"
              required
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="doctor@example.com"
              data-testid="doctor-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Contact Number *</label>
            <input
              type="text"
              required
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground"
              value={formData.contact}
              onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
              placeholder="+91 9876543210"
              data-testid="doctor-contact-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Address *</label>
            <input
              type="text"
              required
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Sector 17, Chandigarh"
              data-testid="doctor-address-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Location *</label>
            <select
              required
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              data-testid="doctor-location-select"
            >
              <option value="Chandigarh">Chandigarh</option>
              <option value="Mohali">Mohali</option>
              <option value="Panchkula">Panchkula</option>
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl bg-muted text-foreground font-semibold hover:bg-muted/80"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90"
              data-testid="save-doctor-btn"
            >
              Add Doctor
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const Appointments = () => {
  const { user } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showAddDoctorModal, setShowAddDoctorModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    appointment_date: '',
    notes: ''
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchDoctors();
    fetchAppointments();
  }, []);

  const fetchDoctors = async () => {
    try {
      const response = await doctorsAPI.getAll();
      setDoctors(response.data);
    } catch (error) {
      console.error('Failed to load doctors:', error);
      toast.error('Failed to load doctors');
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await appointmentsAPI.getAll();
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to load appointments:', error);
    }
  };

  const handleAddDoctor = async (doctorData) => {
    try {
      await doctorsAPI.create(doctorData);
      toast.success('Doctor added successfully!');
      setShowAddDoctorModal(false);
      fetchDoctors();
    } catch (error) {
      toast.error('Failed to add doctor');
    }
  };

  const handleDeleteDoctor = async (doctorId) => {
    if (!window.confirm('Are you sure you want to delete this doctor?')) return;
    
    try {
      await doctorsAPI.delete(doctorId);
      toast.success('Doctor deleted successfully!');
      fetchDoctors();
    } catch (error) {
      toast.error('Failed to delete doctor');
    }
  };

  const handleBookClick = (doctor) => {
    setSelectedDoctor(doctor);
    setShowBookingModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await appointmentsAPI.create({
        provider_name: selectedDoctor.name,
        provider_specialty: selectedDoctor.specialty,
        appointment_date: new Date(formData.appointment_date).toISOString(),
        notes: formData.notes
      });
      toast.success('Appointment scheduled successfully!');
      setShowBookingModal(false);
      setSelectedDoctor(null);
      setFormData({ appointment_date: '', notes: '' });
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to schedule appointment');
    }
  };

  const handleCancel = async (id) => {
    try {
      await appointmentsAPI.update(id, 'cancelled');
      toast.success('Appointment cancelled');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to cancel appointment');
    }
  };

  const filteredDoctors = doctors.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.specialty.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.address.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = locationFilter === 'All' || doc.location === locationFilter;
    return matchesSearch && matchesLocation;
  });

  const locations = ['All', 'Chandigarh', 'Mohali', 'Panchkula'];

  return (
    <div className="min-h-screen bg-background py-8" data-testid="appointments-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Stethoscope className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Find Professional Help
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-4">
            Connect with qualified psychiatrists and psychologists in Chandigarh, Mohali, and Panchkula region
          </p>
          {/* Doctor Summary CTA */}
          <Link
            to="/doctor-summary"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border border-cyan-500/30 rounded-xl hover:from-cyan-500/30 hover:to-blue-500/30 transition-colors"
            data-testid="generate-summary-link"
          >
            <FileText className="w-5 h-5" />
            Prepare for Your Appointment
          </Link>
        </div>

        {/* Admin Add Button */}
        {isAdmin && (
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setShowAddDoctorModal(true)}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90"
              data-testid="add-doctor-btn"
            >
              <Plus className="w-5 h-5" />
              Add Doctor
            </button>
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-card rounded-2xl p-4 mb-8 border border-border">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name, specialty, or address..."
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-background border border-border text-foreground focus:ring-2 focus:ring-primary"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="doctor-search-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {locations.map((loc) => (
                <button
                  key={loc}
                  onClick={() => setLocationFilter(loc)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    locationFilter === loc
                      ? 'bg-primary text-white'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                  data-testid={`filter-${loc.toLowerCase()}`}
                >
                  {loc}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading doctors...</p>
          </div>
        ) : (
          <>
            {/* Doctors Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {filteredDoctors.map((doctor) => (
                <DoctorCard
                  key={doctor.id}
                  doctor={doctor}
                  onBook={handleBookClick}
                  onDelete={handleDeleteDoctor}
                  isAdmin={isAdmin}
                />
              ))}
            </div>

            {filteredDoctors.length === 0 && (
              <div className="text-center py-12 bg-card rounded-2xl border border-border">
                <Search className="w-16 h-16 text-muted mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-2">No professionals found</h3>
                <p className="text-muted-foreground">Try adjusting your search or filter criteria</p>
                {isAdmin && (
                  <button
                    onClick={() => setShowAddDoctorModal(true)}
                    className="mt-4 px-6 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90"
                  >
                    Add First Doctor
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* My Appointments Section */}
        {appointments.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-foreground mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
              My Appointments
            </h2>
            <div className="space-y-4">
              {appointments.map((apt) => (
                <div key={apt.id} className="bg-card rounded-2xl p-6 border border-border" data-testid="appointment-item">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 rounded-full bg-info/10 flex items-center justify-center">
                        <Calendar className="w-7 h-7 text-info" />
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground text-lg">{apt.provider_name}</h3>
                        <p className="text-muted-foreground">{apt.provider_specialty}</p>
                        <p className="text-sm text-foreground mt-2 font-semibold">
                          {new Date(apt.appointment_date).toLocaleString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </p>
                        <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm ${
                          apt.status === 'scheduled' || apt.status === 'confirmed' ? 'bg-success/10 text-success' :
                          apt.status === 'completed' ? 'bg-info/10 text-info' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {apt.status}
                        </span>
                      </div>
                    </div>
                    {(apt.status === 'scheduled' || apt.status === 'confirmed') && (
                      <button
                        onClick={() => handleCancel(apt.id)}
                        className="px-4 py-2 rounded-full bg-destructive/10 text-destructive hover:bg-destructive/20 font-semibold"
                        data-testid="cancel-appointment"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Booking Modal */}
        {showBookingModal && selectedDoctor && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-2xl p-6 w-full max-w-md border border-border" data-testid="booking-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Book Appointment
                </h2>
                <button 
                  onClick={() => setShowBookingModal(false)}
                  className="p-2 hover:bg-muted rounded-lg"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>
              
              <div className="bg-muted/50 rounded-xl p-4 mb-6">
                <h3 className="font-bold text-foreground">{selectedDoctor.name}</h3>
                <p className="text-sm text-primary">{selectedDoctor.specialty}</p>
                <p className="text-sm text-muted-foreground mt-1">{selectedDoctor.address}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Preferred Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:ring-2 focus:ring-primary"
                    value={formData.appointment_date}
                    onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                    data-testid="appointment-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:ring-2 focus:ring-primary resize-none"
                    rows="3"
                    placeholder="Any specific concerns or requirements..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    data-testid="appointment-notes-input"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowBookingModal(false)}
                    className="flex-1 py-3 rounded-xl bg-muted text-foreground font-semibold hover:bg-muted/80"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90"
                    data-testid="confirm-booking-btn"
                  >
                    Confirm Booking
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Doctor Modal */}
        <AddDoctorModal
          isOpen={showAddDoctorModal}
          onClose={() => setShowAddDoctorModal(false)}
          onAdd={handleAddDoctor}
        />
      </div>
    </div>
  );
};
